import {
  createAdminClient,
  createAdminClientGroup,
  createAdminCmsAnnouncement,
  createAdminCmsCarouselSlide,
  createAdminCmsFaqItem,
  createAdminCmsHomeBox,
  createAdminCmsPage,
  createAdminProduct,
  createAdminProductOffer,
  createAdminProductReview,
  createAdminProductShipping,
  createAdminSeoEntry,
  createAdminUser,
  createAdminVoucher,
  deleteAdminClient,
  deleteAdminClientGroup,
  deleteAdminClientNotification,
  deleteAdminCmsAnnouncement,
  deleteAdminCmsCarouselSlide,
  deleteAdminCmsFaqItem,
  deleteAdminCmsHomeBox,
  deleteAdminCmsPage,
  deleteAdminProduct,
  deleteAdminProductOffer,
  deleteAdminProductReview,
  deleteAdminProductShipping,
  deleteAdminSeoEntry,
  deleteAdminUser,
  deleteAdminVoucher,
  getAdminClient,
  getAdminLog,
  getAdminOrder,
  getAdminProduct,
  getAdminReturn,
  getAdminUser,
  getAdminVoucher,
  listAdminClientGroups,
  listAdminClientNotifications,
  listAdminClients,
  listAdminCmsAnnouncements,
  listAdminCmsCarousel,
  listAdminCmsFaq,
  listAdminCmsHomeBoxes,
  listAdminCmsPages,
  listAdminLogs,
  listAdminOrders,
  listAdminProductOffers,
  listAdminProductReviews,
  listAdminProductShipping,
  listAdminProducts,
  listAdminReturns,
  listAdminSeoEntries,
  listAdminUsers,
  listAdminVouchers,
  updateAdminClient,
  updateAdminClientGroup,
  updateAdminClientNotification,
  updateAdminCmsAnnouncement,
  updateAdminCmsCarouselSlide,
  updateAdminCmsFaqItem,
  updateAdminCmsHomeBox,
  updateAdminCmsPage,
  updateAdminProduct,
  updateAdminProductOffer,
  updateAdminProductReview,
  updateAdminProductShipping,
  updateAdminSeoEntry,
  updateAdminUser,
  updateAdminVoucher
} from "@corporatesclub/shared";

type ListParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: number;
  dateFrom?: string;
  dateTo?: string;
};

type ListResult<T> = {items: T[]; total: number; page: number; pageSize: number; totalPages: number};

export type ResourceHandler = {
  list: (params: ListParams) => Promise<ListResult<Record<string, unknown>>>;
  getOne?: (id: number) => Promise<Record<string, unknown>>;
  create?: (data: Record<string, unknown>) => Promise<Record<string, unknown>>;
  update?: (id: number, data: Record<string, unknown>) => Promise<Record<string, unknown>>;
  deleteOne?: (id: number) => Promise<void>;
};

function asList<T extends Record<string, unknown>>(r: {items: T[]; total: number; page: number; pageSize: number; totalPages: number}): ListResult<Record<string, unknown>> {
  return r;
}

export const ADMIN_RESOURCE_HANDLERS: Record<string, ResourceHandler> = {
  orders: {
    list: (p) => listAdminOrders(p).then(asList),
    getOne: (id) => getAdminOrder(id) as Promise<Record<string, unknown>>
  },
  returns: {
    list: (p) => listAdminReturns(p).then(asList),
    getOne: (id) => getAdminReturn(id) as Promise<Record<string, unknown>>
  },
  vouchers: {
    list: (p) => listAdminVouchers(p).then(asList),
    getOne: (id) => getAdminVoucher(id) as Promise<Record<string, unknown>>,
    create: (d) => createAdminVoucher(d) as Promise<Record<string, unknown>>,
    update: (id, d) => updateAdminVoucher(id, d) as Promise<Record<string, unknown>>,
    deleteOne: (id) => deleteAdminVoucher(id).then(() => {})
  },
  products: {
    list: (p) => listAdminProducts(p).then(asList),
    getOne: (id) => getAdminProduct(id) as Promise<Record<string, unknown>>,
    create: (d) => createAdminProduct(d) as Promise<Record<string, unknown>>,
    update: (id, d) => updateAdminProduct(id, d) as Promise<Record<string, unknown>>,
    deleteOne: (id) => deleteAdminProduct(id).then(() => {})
  },
  "product-offers": {
    list: (p) => listAdminProductOffers(p).then(asList),
    create: (d) => createAdminProductOffer(d) as Promise<Record<string, unknown>>,
    update: (id, d) => updateAdminProductOffer(id, d) as Promise<Record<string, unknown>>,
    deleteOne: (id) => deleteAdminProductOffer(id).then(() => {})
  },
  "product-reviews": {
    list: (p) => listAdminProductReviews(p).then(asList),
    create: (d) => createAdminProductReview(d) as Promise<Record<string, unknown>>,
    update: (id, d) => updateAdminProductReview(id, d) as Promise<Record<string, unknown>>,
    deleteOne: (id) => deleteAdminProductReview(id).then(() => {})
  },
  "product-shipping": {
    list: (p) => listAdminProductShipping(p).then(asList),
    create: (d) => createAdminProductShipping(d as {productId: number}) as Promise<Record<string, unknown>>,
    update: (id, d) => updateAdminProductShipping(id, d as {productId: number}) as Promise<Record<string, unknown>>,
    deleteOne: (id) => deleteAdminProductShipping(id).then(() => {})
  },
  clients: {
    list: (p) => listAdminClients(p).then(asList),
    getOne: (id) => getAdminClient(id) as Promise<Record<string, unknown>>,
    create: (d) => createAdminClient(d) as Promise<Record<string, unknown>>,
    update: (id, d) => updateAdminClient(id, d) as Promise<Record<string, unknown>>,
    deleteOne: (id) => deleteAdminClient(id).then(() => {})
  },
  "client-groups": {
    list: (p) => listAdminClientGroups(p).then(asList),
    create: (d) => createAdminClientGroup(d) as Promise<Record<string, unknown>>,
    update: (id, d) => updateAdminClientGroup(id, d) as Promise<Record<string, unknown>>,
    deleteOne: (id) => deleteAdminClientGroup(id).then(() => {})
  },
  "client-notifications": {
    list: (p) => listAdminClientNotifications(p).then(asList),
    update: (id, d) => updateAdminClientNotification(id, d) as Promise<Record<string, unknown>>,
    deleteOne: (id) => deleteAdminClientNotification(id).then(() => {})
  },
  "cms-pages": {
    list: (p) => listAdminCmsPages(p).then(asList),
    create: (d) => createAdminCmsPage(d) as Promise<Record<string, unknown>>,
    update: (id, d) => updateAdminCmsPage(id, d) as Promise<Record<string, unknown>>,
    deleteOne: (id) => deleteAdminCmsPage(id).then(() => {})
  },
  "cms-home-boxes": {
    list: (p) => listAdminCmsHomeBoxes(p).then(asList),
    create: (d) => createAdminCmsHomeBox(d) as Promise<Record<string, unknown>>,
    update: (id, d) => updateAdminCmsHomeBox(id, d) as Promise<Record<string, unknown>>,
    deleteOne: (id) => deleteAdminCmsHomeBox(id).then(() => {})
  },
  "cms-carousel": {
    list: (p) => listAdminCmsCarousel(p).then(asList),
    create: (d) => createAdminCmsCarouselSlide(d) as Promise<Record<string, unknown>>,
    update: (id, d) => updateAdminCmsCarouselSlide(id, d) as Promise<Record<string, unknown>>,
    deleteOne: (id) => deleteAdminCmsCarouselSlide(id).then(() => {})
  },
  "cms-announcements": {
    list: (p) => listAdminCmsAnnouncements(p).then(asList),
    create: (d) => createAdminCmsAnnouncement(d) as Promise<Record<string, unknown>>,
    update: (id, d) => updateAdminCmsAnnouncement(id, d) as Promise<Record<string, unknown>>,
    deleteOne: (id) => deleteAdminCmsAnnouncement(id).then(() => {})
  },
  "cms-faq": {
    list: (p) => listAdminCmsFaq(p).then(asList),
    create: (d) => createAdminCmsFaqItem(d) as Promise<Record<string, unknown>>,
    update: (id, d) => updateAdminCmsFaqItem(id, d) as Promise<Record<string, unknown>>,
    deleteOne: (id) => deleteAdminCmsFaqItem(id).then(() => {})
  },
  seo: {
    list: (p) => listAdminSeoEntries(p).then(asList),
    create: (d) => createAdminSeoEntry(d) as Promise<Record<string, unknown>>,
    update: (id, d) => updateAdminSeoEntry(id, d) as Promise<Record<string, unknown>>,
    deleteOne: (id) => deleteAdminSeoEntry(id).then(() => {})
  },
  users: {
    list: (p) => listAdminUsers(p).then(asList),
    getOne: (id) => getAdminUser(id) as Promise<Record<string, unknown>>,
    create: (d) => createAdminUser(d as Parameters<typeof createAdminUser>[0]) as Promise<Record<string, unknown>>,
    update: (id, d) => updateAdminUser(id, d) as Promise<Record<string, unknown>>,
    deleteOne: (id) => deleteAdminUser(id).then(() => {})
  },
  logs: {
    list: (p) => listAdminLogs(p).then(asList),
    getOne: (id) => getAdminLog(id) as Promise<Record<string, unknown>>
  }
};
